import { LEAD_DROP_POPUP_9_CONSTANTS } from "../leadDropPopup/leadDropPopup_9";

/**
 * Lead Drop Form 9 — embeddable (inline) twin of Lead Drop Popup 9.
 *
 * Same "Apply Now" single-step flow (optional countdown → OTP → last card with the
 * configurable circular SVG background) as popup 9, configured with its OWN row so
 * the inline form and the modal popup hold independent settings. Popup-internal
 * fields (display intervals + delay/exit-intent toggles) have no meaning for an
 * always-rendered inline form, so they are stripped here.
 */
const POPUP_INTERNAL_FIELDS = new Set([
  "popup_display_interval_1",
  "popup_display_interval_2",
  "popup_display_interval_3",
  "enable_delay_form",
  "enable_exit_form",
]);

const stripPopupInternalKeys = (row: Record<string, unknown>) => {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!POPUP_INTERNAL_FIELDS.has(key)) next[key] = value;
  }
  return next;
};

/**
 * The fields config is inherited from popup 9, whose field names map to
 * `lead_drop_popup` DB columns. The `lead_drop_form` table has a DIFFERENT column
 * set, so any inherited popup-column field saved top-level is silently dropped by
 * Sequelize. Only these names exist as real columns on BOTH tables; every other
 * inherited field must be flagged `isJsonField` so Form_4 nests it under
 * `additional_fields`, which the server persists and flattens back for the widget.
 */
const LEAD_DROP_FORM_SHARED_COLUMNS = new Set([
  "checkbox_description",
  "button_background_color",
  // Consent links inherited from popup 9; kept top-level as real lead_drop_form columns.
  "terms_conditions_link",
  "privacy_policy_link",
]);

/**
 * Aligns an inherited popup-9 field with the `lead_drop_form` column set in BOTH
 * directions. Nesting-only was not enough: a name can be a real column here while
 * popup 9 has to flag it `isJsonField` because it is NOT a `lead_drop_popup`
 * column (`button_background_color`), and inheriting that flag would push a value
 * this table can store natively into `additional_fields` instead.
 */
const alignWithFormColumns = <
  T extends { fieldName?: unknown; isJsonField?: boolean },
>(
  field: T,
): T => {
  if (typeof field.fieldName !== "string") return field;
  if (LEAD_DROP_FORM_SHARED_COLUMNS.has(field.fieldName)) {
    return field.isJsonField ? { ...field, isJsonField: false } : field;
  }
  return field.isJsonField ? field : { ...field, isJsonField: true };
};

export const LEAD_DROP_FORM_9_CONSTANTS = {
  DEFAULT_ROW: stripPopupInternalKeys(
    LEAD_DROP_POPUP_9_CONSTANTS.DEFAULT_ROW as Record<string, unknown>,
  ),

  LEAD_DROP_FORM_FIELDS_CONFIG:
    LEAD_DROP_POPUP_9_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG.filter(
      (field) =>
        typeof field.fieldName !== "string" ||
        !POPUP_INTERNAL_FIELDS.has(field.fieldName),
    ).map(alignWithFormColumns),

  METADATA_FIELDS: LEAD_DROP_POPUP_9_CONSTANTS.METADATA_FIELDS,
};
